import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * The app-wide "sidebar-like" overlay: a floating glass panel pinned to the
 * right edge, OVER the page (never pushing content), glassy in both modes.
 *
 * Built as a NON-modal Radix dialog, which is exactly its contract:
 *  - Esc closes; clicking outside does NOT — it is a working surface the user
 *    keeps open while acting on the page behind it (`onInteractOutside` is
 *    swallowed);
 *  - no focus trap and no focus steal on open/close: the caret stays where
 *    the user left it, in the panel or in the document.
 */
interface GlassPanelProps extends Omit<ComponentPropsWithoutRef<typeof DialogPrimitive.Content>, 'title' | 'content'> {
    open: boolean;
    onClose: () => void;
    title: ReactNode;
    /** Extra header row content, between the title and the close button. */
    headerExtras?: ReactNode;
    children: ReactNode;
}

export function GlassPanel({ open, onClose, title, headerExtras, className, children, ...contentProps }: GlassPanelProps) {
    return (
        <DialogPrimitive.Root
            open={open}
            modal={false}
            onOpenChange={nextOpen => {
                if (!nextOpen) onClose();
            }}
        >
            <DialogPrimitive.Portal>
                <DialogPrimitive.Content
                    aria-describedby={undefined}
                    // Radix stamps `role="dialog"` on this content, and the
                    // editor's modal guards (⌘⇧M's isModalOpen) must NOT count
                    // a non-modal working surface as a modal — this attribute
                    // is what they exclude by.
                    data-glass-panel=""
                    className={cn(
                        'fixed bottom-3 right-3 top-3 z-[1300] flex w-[440px] max-w-[calc(100vw-24px)] flex-col overflow-hidden rounded-2xl',
                        'bg-bg-paper/75 backdrop-blur-[30px] dark:bg-c17/95',
                        'shadow-[0_12px_48px_rgba(0,0,0,0.18)] dark:shadow-[0_12px_48px_rgba(0,0,0,0.5)]',
                        'motion-safe:data-[state=open]:animate-glass-panel-in motion-safe:data-[state=closed]:animate-glass-panel-out',
                        className,
                    )}
                    onOpenAutoFocus={event => event.preventDefault()}
                    onCloseAutoFocus={event => event.preventDefault()}
                    onPointerDownOutside={event => event.preventDefault()}
                    onInteractOutside={event => event.preventDefault()}
                    {...contentProps}
                >
                    <header className="flex items-center gap-2 px-5 pb-2 pt-4">
                        <DragIndicatorIcon aria-hidden className="text-[1.2rem] text-c79 dark:text-c4" />
                        <DialogPrimitive.Title className="m-0 text-xl font-semibold text-c16 dark:text-c1">{title}</DialogPrimitive.Title>
                        {headerExtras}
                        <DialogPrimitive.Close
                            aria-label="close"
                            className="ml-auto flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border-none bg-transparent p-0 text-c16 hover:bg-c20/8 dark:text-c1 dark:hover:bg-c20/8"
                        >
                            <CloseIcon className="text-[1.3rem]" />
                        </DialogPrimitive.Close>
                    </header>
                    <div className="flex min-h-0 flex-1 flex-col">{children}</div>
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
}
